/**
 * Extract metadata from file objects
 * Supports images, videos, and audio files
 */

interface ImageMetadata {
  width: number;
  height: number;
  size: number;
}

interface VideoMetadata {
  width: number;
  height: number;
  size: number;
  duration: number;
}

interface AudioMetadata {
  size: number;
  duration: number;
}

type FileMetadata = ImageMetadata | VideoMetadata | AudioMetadata;

/**
 * Extract image metadata (width, height, size) from a file
 */
export async function extractImageMetadata(file: File): Promise<ImageMetadata | null> {
  return new Promise((resolve) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        resolve({
          width: img.width,
          height: img.height,
          size: file.size,
        });
      };
      img.onerror = () => {
        console.warn('Failed to extract image dimensions');
        resolve(null);
      };
      img.src = e.target?.result as string;
    };

    reader.onerror = () => {
      console.warn('Failed to read image file');
      resolve(null);
    };

    reader.readAsDataURL(file);
  });
}

/**
 * Extract video metadata (width, height, size, duration) from a file
 */
export async function extractVideoMetadata(file: File): Promise<VideoMetadata | null> {
  return new Promise((resolve) => {
    const video = document.createElement('video');
    const fileUrl = URL.createObjectURL(file);

    video.onloadedmetadata = () => {
      resolve({
        width: video.videoWidth,
        height: video.videoHeight,
        size: file.size,
        duration: video.duration,
      });
      URL.revokeObjectURL(fileUrl);
    };

    video.onerror = () => {
      console.warn('Failed to extract video metadata');
      URL.revokeObjectURL(fileUrl);
      resolve(null);
    };

    video.src = fileUrl;
  });
}

/**
 * Extract audio metadata (size, duration) from a file
 */
export async function extractAudioMetadata(file: File): Promise<AudioMetadata | null> {
  return new Promise((resolve) => {
    const audio = new Audio();
    const fileUrl = URL.createObjectURL(file);

    audio.onloadedmetadata = () => {
      resolve({
        size: file.size,
        duration: audio.duration,
      });
      URL.revokeObjectURL(fileUrl);
    };

    audio.onerror = () => {
      console.warn('Failed to extract audio metadata');
      URL.revokeObjectURL(fileUrl);
      resolve(null);
    };

    audio.src = fileUrl;
  });
}

/**
 * Extract metadata based on file type
 * Automatically detects whether it's an image, video, or audio file
 */
export async function extractFileMetadata(
  file: File,
  format: string
): Promise<FileMetadata | null> {
  try {
    if (format === 'image') {
      return await extractImageMetadata(file);
    } else if (format === 'video') {
      return await extractVideoMetadata(file);
    } else if (format === 'audio') {
      return await extractAudioMetadata(file);
    }
    return null;
  } catch (error) {
    console.error('Error extracting file metadata:', error);
    return null;
  }
}
